import type { ReactNode } from 'react';
import { cx } from './cx.ts';
import { DateFlag } from './DayHeader.tsx';
import s from './TripParts.module.css';
import t from './type.module.css';

// Trip-level components from S-02 / S-03 / S-08 / W-02 (proposed for the D-4 review), matching
// PFDesign/TripParts.swift.

/** `11.03 ━━━ ▶ 11.12` (REF-21's arrow bar): the bar is signal only while the trip is on. */
export function DateRangeRule({
  start,
  end,
  live = false,
  large = false,
}: {
  start: string;
  end: string;
  live?: boolean;
  large?: boolean;
}) {
  return (
    <span
      className={cx(s.rule, large ? t.readoutL : t.readoutM)}
      role="img"
      aria-label={`${start} to ${end}`}
    >
      <span>{start}</span>
      <span className={cx(s.bar, large && s.barLarge, live && s.live)} />
      <span className={s.arrow}>▶</span>
      <span>{end}</span>
    </span>
  );
}

/** `JP26-A │ DAY 06 / 10`: the trip card's tag. */
export function CodeTag({ code, status }: { code: string; status: string }) {
  return (
    <span className={cx(s.codeTag, t.readoutS)}>
      <span className={s.code}>{code}</span>
      <span className={s.status}>{status.toUpperCase()}</span>
    </span>
  );
}

/** C-11 Trip card (S-02): cover band (duotone while travelling) with the code tag, title, the
 *  date rule with places, and an optional footer. */
export function TripCard({
  code,
  status,
  title,
  start,
  end,
  detail,
  todayFlag,
  cover,
  live = false,
  children,
}: {
  code: string;
  status: string;
  title: string;
  start: string;
  end: string;
  detail?: string;
  todayFlag?: string;
  cover?: string;
  live?: boolean;
  children?: ReactNode;
}) {
  return (
    <article className={s.card}>
      <div className={cx(s.band, cover ? s.withCover : s.noCover, live && s.liveBand)}>
        {cover && <img src={cover} alt="" className={s.cover} />}
        <CodeTag code={code} status={status} />
      </div>
      <div className={s.cardBody}>
        <div className={s.titleRow}>
          <h3 className={cx(t.titleL, s.flat)}>{title}</h3>
          {todayFlag && <DateFlag text={todayFlag} today />}
        </div>
        <div className={s.dates}>
          <DateRangeRule start={start} end={end} />
          {detail && <span className={cx(t.bodyS, s.muted)}>· {detail}</span>}
        </div>
        {children}
      </div>
    </article>
  );
}

/** A label over a value (NEXT, SHARED SPEND). */
export function LabeledReadout({
  label,
  value,
  secondary,
  trailing = false,
}: {
  label: string;
  value: string;
  secondary?: string;
  trailing?: boolean;
}) {
  return (
    <div className={cx(s.readout, trailing && s.trailing)}>
      <span className={cx(t.label, s.faint)}>{label}</span>
      <span>
        <span className={t.titleM}>{value}</span>
        {secondary && <span className={cx(t.readoutS, s.muted)}> {secondary}</span>}
      </span>
    </div>
  );
}

/** C-17 Stat tiles (S-03, W-03): big numerals with caps labels, split by hairlines. */
export function StatTiles({ tiles }: { tiles: { label: string; value: string; unit?: string }[] }) {
  return (
    <dl className={s.tiles}>
      {tiles.map((tile) => (
        <div key={tile.label} className={s.tile}>
          <dt className={cx(t.label, s.faint)}>{tile.label}</dt>
          <dd className={s.flat}>
            <span className={t.readoutL}>{tile.value}</span>
            {tile.unit && <span className={cx(t.bodyS, s.muted)}> {tile.unit}</span>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** The title block (S-03): a ruled 2-column table of the trip's facts. */
export function TitleBlock({ cells }: { cells: [string, string][] }) {
  return (
    <dl className={s.titleBlock}>
      {cells.map(([label, value]) => (
        <div key={label} className={s.cell}>
          <dt className={cx(t.label, s.faint)}>{label}</dt>
          <dd className={cx(t.body, s.flat)}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** C-19 Budget meter (S-08, W-02): ten segments in ink; over budget, every segment is danger. */
export function BudgetMeter({ fraction, segments = 10 }: { fraction: number; segments?: number }) {
  const over = fraction > 1;
  const filled = over ? segments : Math.floor(Math.min(Math.max(fraction, 0), 1) * segments + 1e-9);
  return (
    <span
      className={s.meter}
      role="img"
      aria-label={over ? 'Over budget' : `${Math.round(fraction * 100)} percent used`}
    >
      {Array.from({ length: segments }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed segments that never reorder
        <i key={i} className={cx(over ? s.over : i < filled && s.filled)} />
      ))}
    </span>
  );
}

/** A continuous share bar (by-category rows). */
export function ShareBar({ fraction }: { fraction: number }) {
  return (
    <span className={s.share} aria-hidden="true">
      <i style={{ width: `${Math.min(Math.max(fraction, 0), 1) * 100}%` }} />
    </span>
  );
}

/** C-10 Status tags (W-02): PAID filled ink, DUE on signal with a stripe block and its date,
 *  PLANNED outlined. */
export function StatusTag({ status, date }: { status: 'paid' | 'due' | 'planned'; date?: string | null }) {
  if (status === 'paid') return <span className={cx(s.tag, s.paid, t.label)}>Paid</span>;
  if (status === 'planned') return <span className={cx(s.tag, s.planned, t.label)}>Planned</span>;
  return (
    <span className={s.dueGroup}>
      <span className={s.stripes} aria-hidden="true" />
      <span className={cx(s.tag, s.due, t.label)}>Due</span>
      {date && <span className={cx(s.tag, s.dueDate, t.readoutS)}>{date}</span>}
    </span>
  );
}

/** The hazard edge (M-18) for rows that need attention: add to the row's className. */
export const hazardEdge = s.hazardEdge;
